import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { formatDate, exportSummaryToPDF, exportSummaryToExcel } from "../../../utils/exportUtils";
import { hoverScale, modalButton } from "../../../components/DesignStandardize";
import AttendanceDetailsView from "./AttendanceDetailsView";
import "../../../components/Scrollbar.css";

const AttendanceSummaryView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const employeeIDFromURL = searchParams.get('employeeID');
  const monthFromURL = searchParams.get('month');

  // Helper function for late attendance color coding
  const getLateColorClass = (totalLateDays) => {
    if (totalLateDays > 3) return "border-l-4 border-red-500 bg-red-50";
    if (totalLateDays >= 1 && totalLateDays <= 3) return "border-l-4 border-yellow-500 bg-yellow-50";
    return "bg-green-50";
  };

  // Helper function for leave color coding
  const getLeaveColorClass = () => "border-l-4 border-blue-500 bg-blue-50";

  // Helper function to convert MM/YYYY to readable format
  const formatMonthDisplay = (monthString) => {
    if (!monthString) {
      // If no month selected, use current month
      const now = new Date();
      return now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    const [mm, yyyy] = monthString.split('/');
    const date = new Date(yyyy, mm - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Helper function to get month name only (abbreviated)
  const getMonthName = (monthString) => {
    if (!monthString) {
      const now = new Date();
      return now.toLocaleDateString('en-US', { month: 'short' });
    }
    const [mm, yyyy] = monthString.split('/');
    const date = new Date(yyyy, mm - 1);
    return date.toLocaleDateString('en-US', { month: 'short' });
  };

  // Helper function to get YTD range (January to current month) - abbreviated
  const getYTDRange = (monthString) => {
    if (!monthString) {
      const now = new Date();
      const currentMonth = now.toLocaleDateString('en-US', { month: 'short' });
      return `Jan-${currentMonth}`;
    }
    const [mm, yyyy] = monthString.split('/');
    const date = new Date(yyyy, mm - 1);
    const currentMonth = date.toLocaleDateString('en-US', { month: 'short' });
    return `Jan-${currentMonth}`;
  };

  // Helper function to generate month options
  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    
    // Generate last 12 months
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yyyy = date.getFullYear();
      const value = `${mm}/${yyyy}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    
    return options;
  };

  const [summaryData, setSummaryData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(monthFromURL || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  const fetchSummaryData = async (month) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("jwtToken");
      const endpoint = month
        ? `${apiUrl}/summary?month=${month}`
        : `${apiUrl}/summary`;

      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `${token}`,
        },
      });

      setSummaryData(response.data.summary || []);
    } catch (err) {
      console.error("Error fetching summary data:", err);

      if (err.response?.status === 401) {
        setError("Session expired. Please log in again.");
        localStorage.clear();
        window.location.href = "/";
      } else if (err.response?.status === 500) {
        setError("An error occurred while fetching data. Please try again.");
      } else {
        setError(
          "Unable to connect to server. Please check your connection."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    fetchSummaryData(month);
  };

  const handleExportPDF = () => {
    try {
      const displayMonth = formatMonthDisplay(selectedMonth);
      exportSummaryToPDF(summaryData, displayMonth);
    } catch (error) {
      console.error("PDF export failed:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  const handleExportExcel = async () => {
    try {
      const displayMonth = formatMonthDisplay(selectedMonth);
      await exportSummaryToExcel(summaryData, displayMonth);
    } catch (error) {
      console.error("Excel export failed:", error);
      alert("Failed to generate Excel file. Please try again.");
    }
  };

  const handleBack = () => {
    // Remove employeeID from URL to go back to summary
    setSearchParams(selectedMonth ? { month: selectedMonth } : {});
  };

  useEffect(() => {
    if (!employeeIDFromURL) {
      fetchSummaryData(selectedMonth);
    }
  }, [employeeIDFromURL, selectedMonth]);

  // If an employee is selected via URL, render the details view
  if (employeeIDFromURL) {
    return (
      <AttendanceDetailsView
        employeeID={employeeIDFromURL}
        initialMonth={selectedMonth}
        onBack={handleBack}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600">
          Loading attendance summary...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <p className="text-red-500 text-lg font-semibold">{error}</p>
          <button
            onClick={() => fetchSummaryData(selectedMonth)}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!loading && summaryData.length === 0) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <p className="text-gray-500 text-lg">
            No attendance data available for{" "}
            {selectedMonth || "current month"}
          </p>
          <p className="text-gray-400 text-sm mt-2">
            Try selecting a different month
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {/* Header Section - Fixed */}
      <div className="flex-shrink-0 space-y-3 pb-3">
        <div className="text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
            Attendance Summary - {formatMonthDisplay(selectedMonth)}
          </h2>
        </div>

        {/* Month Filter */}
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
          <label className="font-medium text-sm sm:text-base">Select Month:</label>
          <select
            className="border rounded px-3 py-2 w-full sm:w-auto bg-white"
            value={selectedMonth}
            onChange={(e) => handleMonthChange(e.target.value)}
          >
            <option value="">Current Month</option>
            {generateMonthOptions().map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Export Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <button
            onClick={handleExportPDF}
            className={`${hoverScale} ${modalButton} text-sm sm:text-base`}
            disabled={summaryData.length === 0}
          >
            Export to PDF
          </button>
          <button
            onClick={handleExportExcel}
            className={`${hoverScale} ${modalButton} text-sm sm:text-base`}
            disabled={summaryData.length === 0}
          >
            Export to Excel
          </button>
        </div>
      </div>

      {/* Table Section - Scrollable */}
      <div className="flex-1 min-h-0 w-full">
        <div className="h-full w-full border border-gray-300 rounded-lg overflow-auto">
          <table className="w-full bg-white shadow-sm text-xs border-collapse">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-700 border-b whitespace-nowrap sticky left-0 bg-gray-100 z-10">
                Sl
              </th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-700 border-b whitespace-nowrap sticky left-8 bg-gray-100 z-10">
                Name
              </th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-700 border-b whitespace-nowrap">
                Designation
              </th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-700 border-b whitespace-nowrap">
                Join Date
              </th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-700 border-b whitespace-nowrap">
                Leave ({getYTDRange(selectedMonth)})
              </th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-700 border-b whitespace-nowrap">
                Leave Dates ({getMonthName(selectedMonth)})
              </th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-700 border-b whitespace-nowrap">
                Total Leave ({getMonthName(selectedMonth)})
              </th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-700 border-b whitespace-nowrap">
                Late ({getYTDRange(selectedMonth)})
              </th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-700 border-b whitespace-nowrap">
                Late Dates ({getMonthName(selectedMonth)})
              </th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-700 border-b whitespace-nowrap">
                Total Late ({getMonthName(selectedMonth)})
              </th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-700 border-b whitespace-nowrap">
                Absent ({getYTDRange(selectedMonth)})
              </th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-700 border-b whitespace-nowrap">
                Absent Dates ({getMonthName(selectedMonth)})
              </th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-700 border-b whitespace-nowrap">
                Total Absent ({getMonthName(selectedMonth)})
              </th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-700 border-b whitespace-nowrap">
                Missing Task ({getYTDRange(selectedMonth)})
              </th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-700 border-b whitespace-nowrap">
                Missing Task Dates ({getMonthName(selectedMonth)})
              </th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-700 border-b whitespace-nowrap">
                Total Missing Task ({getMonthName(selectedMonth)})
              </th>
            </tr>
          </thead>
          <tbody>
            {summaryData.map((employee, index) => (
              <tr
                key={employee.employeeID}
                className={`hover:bg-gray-100 cursor-pointer transition-colors ${
                  index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                }`}
                onClick={() => {
                  const params = { employeeID: employee.employeeID };
                  if (selectedMonth) params.month = selectedMonth;
                  setSearchParams(params);
                }}
              >
                <td className="px-2 py-2 text-[10px] text-gray-800 border-b whitespace-nowrap sticky left-0 bg-inherit">
                  {employee.slNo}
                </td>
                <td className="px-2 py-2 text-[10px] text-gray-800 border-b whitespace-nowrap sticky left-8 bg-inherit">
                  {employee.name}
                </td>
                <td className="px-2 py-2 text-[10px] text-gray-800 border-b whitespace-nowrap">
                  {employee.designation}
                </td>
                <td className="px-2 py-2 text-[10px] text-gray-800 border-b whitespace-nowrap">
                  {formatDate(employee.joiningDate)}
                </td>
                <td className="px-2 py-2 text-[10px] text-gray-800 border-b whitespace-nowrap">
                  {employee.leaveTakenYTD || 0}
                </td>
                <td className={`px-2 py-2 text-[10px] text-gray-800 border-b ${getLeaveColorClass()}`}>
                  {employee.leaveDatesCurrent || "-"}
                </td>
                <td className={`px-2 py-2 text-[10px] text-gray-800 border-b whitespace-nowrap ${getLeaveColorClass()}`}>
                  {employee.totalLeaveCurrent}
                </td>
                <td className="px-2 py-2 text-[10px] text-gray-800 border-b whitespace-nowrap">
                  {employee.totalLateDaysYTD || 0}
                </td>
                <td className={`px-2 py-2 text-[10px] text-gray-800 border-b ${getLateColorClass(employee.totalLateDays)}`}>
                  {employee.lateDatesBreakdown || "-"}
                </td>
                <td className={`px-2 py-2 text-[10px] text-gray-800 border-b whitespace-nowrap ${getLateColorClass(employee.totalLateDays)}`}>
                  {employee.totalLateDays}
                </td>
                <td className="px-2 py-2 text-[10px] text-gray-800 border-b whitespace-nowrap">
                  {employee.totalAbsentDaysYTD || 0}
                </td>
                <td className="px-2 py-2 text-[10px] text-gray-800 border-b border-l-4 border-gray-400 bg-gray-50">
                  {employee.absentDatesCurrent || "-"}
                </td>
                <td className="px-2 py-2 text-[10px] text-gray-800 border-b whitespace-nowrap border-l-4 border-gray-400 bg-gray-50">
                  {employee.absentDaysCurrent || 0}
                </td>
                <td className="px-2 py-2 text-[10px] text-gray-800 border-b whitespace-nowrap">
                  {employee.missingTaskDaysYTD || 0}
                </td>
                <td className="px-2 py-2 text-[10px] text-gray-800 border-b border-l-4 border-orange-400 bg-orange-50">
                  {employee.missingTaskDatesCurrent || "-"}
                </td>
                <td className="px-2 py-2 text-[10px] text-gray-800 border-b whitespace-nowrap border-l-4 border-orange-400 bg-orange-50">
                  {employee.missingTaskDaysCurrent || 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

export default AttendanceSummaryView;
